/**
 * Zutatenzeilen lesen („200 g Mehl Type 405“), für die Schnelleingabe im
 * Editor, die Einkaufsliste und den Import von Webseiten.
 *
 * Eine Zutat je Zeile, zum Beispiel "200 g Mehl Type 405". Menge, Einheit und
 * Name werden erkannt. Ohne Mengenangabe gilt die Zutat als "nach Geschmack".
 */
import type { ScalingMode as Scaling } from './types.ts';

/** Eine erkannte Zutat. */
export interface ParsedIngredient {
  readonly amount: string;
  readonly unit: string;
  readonly name: string;
  readonly note: string;
  readonly scaling: Scaling;
}

/** Bekannte Einheiten der Schnelleingabe. */
const UNITS: readonly string[] = [
  'kg',
  'g',
  'mg',
  'ml',
  'l',
  'tl',
  'el',
  'tasse',
  'tassen',
  'prise',
  'priese',
  'stück',
  'stk',
  'st',
  'bund',
  'zehe',
  'zehen',
  'scheibe',
  'scheiben',
  'dose',
  'dosen',
  'päckchen',
  'packung',
  'becher',
  'glas',
  'flasche',
  'handvoll',
  'würfel',
  'pkg',
];

/** Gewürze und Kleinmengen wachsen nicht linear mit. */
const SPICES: readonly string[] = [
  'salz',
  'pfeffer',
  'muskat',
  'zimt',
  'paprika',
  'curry',
  'oregano',
  'thymian',
  'rosmarin',
  'kümmel',
  'chili',
  'vanille',
  'backpulver',
  'natron',
  'hefe',
  'zimt',
  'nelken',
  'kardamom',
  'ingwer',
  'knoblauch',
  'zucker',
  'honig',
  'senf',
  'essig',
  'sojasauce',
  'sauce',
];

/** Wählt die passende Skalierung für eine Zutat. */
export function scalingFor(name: string, unit: string, amount: string): Scaling {
  const lower = name.toLowerCase();
  const normalizedUnit = unit.toLowerCase();
  if (amount === '') {
    return 'fixed';
  }
  if (normalizedUnit === 'prise' || normalizedUnit === 'priese') {
    return 'fixed';
  }
  if (normalizedUnit === 'stück' || normalizedUnit === 'stk' || normalizedUnit === 'st') {
    return 'rounded';
  }
  if (SPICES.some((spice) => lower.includes(spice))) {
    return 'sublinear';
  }
  return 'linear';
}

/** Erkennt Mengenangaben: Dezimalzahlen, Brüche und gemischte Brüche. */
const AMOUNT_PATTERN = '(\\d+\\s+\\d+\\s*/\\s*\\d+|\\d+\\s*/\\s*\\d+|\\d+[.,]\\d+|\\d+|[½¼¾⅓⅔⅛⅜⅝⅞])';

/** Wandelt deutsche Bruchschreibweisen in Zahlen um, zum Beispiel "1/2" oder "1 1/2". */
function parseAmount(value: string): string {
  const cleaned = value.trim();
  if (cleaned === '') {
    return '';
  }

  const fraction = cleaned.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (fraction !== null) {
    const whole = Number(fraction[1]);
    const numerator = Number(fraction[2]);
    const denominator = Number(fraction[3]);
    if (denominator !== 0) {
      return String(whole + numerator / denominator).replace('.', ',');
    }
  }

  const simple = cleaned.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (simple !== null) {
    const numerator = Number(simple[1]);
    const denominator = Number(simple[2]);
    if (denominator !== 0) {
      return String(numerator / denominator).replace('.', ',');
    }
  }

  return cleaned.replace('.', ',');
}

/** Zerlegt eine Zeile in Menge, Einheit, Name und Zusatz. */
export function parseIngredientLine(line: string): ParsedIngredient | null {
  const trimmed = line.trim();
  if (trimmed === '') {
    return null;
  }

  // Zusätze in Klammern oder nach einem Komma wandern in das Zusatzfeld.
  let note = '';
  let rest = trimmed;
  const bracket = rest.match(/\(([^)]+)\)\s*$/);
  if (bracket !== null && bracket[1] !== undefined) {
    note = bracket[1].trim();
    rest = rest.slice(0, bracket.index).trim();
  }
  const comma = rest.indexOf(',');
  if (comma > 0) {
    note = [note, rest.slice(comma + 1).trim()].filter((entry) => entry !== '').join(', ');
    rest = rest.slice(0, comma).trim();
  }

  // Menge und Einheit am Anfang, zum Beispiel "200 g" oder "1 1/2 TL".
  const match = rest.match(new RegExp(`^${AMOUNT_PATTERN}\\s*([A-Za-zÄÖÜäöüß.]*)\\s*(.*)$`));
  if (match === null) {
    return { amount: '', unit: '', name: rest, note, scaling: 'fixed' };
  }

  const rawAmount = (match[1] ?? '').trim();
  const rawUnit = (match[2] ?? '').replace(/\.$/, '').trim();
  const name = (match[3] ?? '').trim();

  const amount = parseAmount(rawAmount);
  const unit = UNITS.includes(rawUnit.toLowerCase()) ? rawUnit : '';
  const finalName = unit === '' && rawUnit !== '' ? `${rawUnit} ${name}`.trim() : name;

  return {
    amount,
    unit,
    name: finalName === '' ? rest : finalName,
    note,
    scaling: scalingFor(finalName, unit, amount),
  };
}

/** Zerlegt mehrere Zeilen. */
export function parseIngredientLines(text: string): ParsedIngredient[] {
  return text
    .split('\n')
    .map((line) => parseIngredientLine(line))
    .filter((entry): entry is ParsedIngredient => entry !== null);
}
